<template>
    <Teleport to="body">
        <div class="st-modal" :class="{ 'st-modal--active': open }">
            <div class="st-modal__content">
                <div class="st-modal__header">
                    <div class="hero__header">Bekreft tur</div>
                </div>
                <div v-if="route" class="st-modal__text">
                    <div v-if="error" class="hero__error hero__error--date">{{ error }}</div>

                    <div class="form__routeheader"><span class="form__routename">{{ route.name }}</span></div>
                    
                    <div v-if="route.public && !route.opened" class="form__fields-center form__competition-end" style="color:red; text-align:center;">
                        <p>Det er ikke mulig å registrere turer før de er åpnet. <br /> Åpningsdato for denne turen er: <b>{{ route.opening_out }}</b></p>
                    </div>

                    <div v-if="dateBounds?.season_ended_text" class="form__competition-end">{{ dateBounds.season_ended_text }}</div>

                    <div v-if="!route.public" class="form__competition-end">Denne turen er ikke åpen, og kan derfor ikke registreres.</div>

                    <template v-if="route.public && route.opened">
                        <div class="form__fields-center">
                            <div class="form__fields-group">
                                <div class="form__routedate">
                                    <label for="register-trip-date" class="form__reglabel--single">Dato:</label>
                                    <Calendar
                                        inputId="register-trip-date"
                                        v-model="selectedDate"
                                        dateFormat="dd.mm.yy"
                                        :showIcon="true"
                                        :minDate="minDate"
                                        :maxDate="maxDate"
                                        appendTo="body"
                                        :baseZIndex="100002"
                                    />
                                </div>

                                <div v-if="familyMembers.length > 1" class="form__companions">
                                    <div class="form__companions--header">Turfølge:</div>
                                    <div v-for="member in familyMembers" :key="member.id" class="form__companion">
                                        <div class="form__option">
                                            <label class="form__checkbox">
                                                <input type="checkbox" :value="member.id" v-model="selectedMembers">
                                                <span>{{ member.name }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>

                    <div class="mypage__editbar">
                        <button
                            v-if="route.public && route.opened"
                            type="button"
                            class="action"
                            :disabled="submitting"
                            @click="$emit('confirm', { date: isoDate, members: selectedMembers })"
                        >{{ submitting ? 'Registrerer...' : 'Bekreft' }}</button>
                        <button type="button" class="action action--hollow" :disabled="submitting" @click="$emit('cancel')">{{ route.public && route.opened ? 'Avbryt' : 'Lukk' }}</button>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import Calendar from 'primevue/calendar';

const props = defineProps({
    open: { type: Boolean, required: true },
    route: { type: Object, default: null },
    dateBounds: { type: Object, default: null },
    familyMembers: { type: Array, default: () => [] },
    personId: { type: Number, required: true },
    submitting: { type: Boolean, default: false },
    error: { type: String, default: null },
});
defineEmits(['cancel', 'confirm']);

// Calendar (the same PrimeVue component the admin date fields use - see
// mountCalendar() in site/admin/js/src/admin.js) works in terms of Date
// objects, not the "YYYY-MM-DD" strings the backend sends/expects.
function parseIsoDate(value) {
    return value ? new Date(value) : null;
}

function toIsoDate(value) {
    if (!value) {
        return '';
    }
    return value.getFullYear() + '-' + String(value.getMonth() + 1).padStart(2, '0') + '-' + String(value.getDate()).padStart(2, '0');
}

const selectedDate = ref(null);
const selectedMembers = ref([props.personId]);
const minDate = computed(() => parseIsoDate(props.dateBounds?.min));
const maxDate = computed(() => parseIsoDate(props.dateBounds?.max));
const isoDate = computed(() => toIsoDate(selectedDate.value));

// A fresh lookup (new route/dateBounds) means a fresh confirm step - reset
// the date/companion selection rather than carrying over the previous one.
watch(() => props.route, (newRoute) => {
    if (newRoute) {
        selectedDate.value = parseIsoDate(props.dateBounds?.value);
        selectedMembers.value = [props.personId];
    }
});
</script>

<style scoped>
/* form__reglabel--single/form__companions are normally centered/inline
   (see app/scss/blocks/_form.scss) - this is the only remaining user of
   form__routedate/form__companions in this shape, so it's safe to override
   here rather than touching the shared classes. form__fields-center centers
   the date-field + companions block as a single unit; form__fields-group is
   the actual box both share a left edge with. */
.form__fields-center {
    display: flex;
    justify-content: center;
}
.form__fields-group {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}
.form__fields-group .form__reglabel--single {
    display: block;
    width: auto;
    text-align: left;
    margin-bottom: 0.4rem;
}
.form__fields-group .form__companions {
    text-align: left;
    width: 100%;
}
.form__fields-group .form__companions .form__option {
    display: block;
    text-align: left;
}
</style>
